import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Crown, Check, Sparkles, Zap, Brain, 
  TrendingUp, Receipt, ArrowRight, Loader2 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ELITE_FEATURES = [
  { icon: Brain, text: 'AI-Powered Budget Predictions' },
  { icon: Receipt, text: 'Unlimited Receipt Scanning' },
  { icon: TrendingUp, text: 'Smart Spending Analytics' },
  { icon: Zap, text: 'Personalized Savings Tips' },
];

export function TrialOffer({ onStartTrial, onSkip }) {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/subscription/start-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        toast.success('Welcome to your 14-day Elite trial!');
        onStartTrial();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to start trial');
        onSkip(); // Continue anyway
      }
    } catch (error) {
      console.error('Trial error:', error);
      onSkip(); // Continue anyway on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-violet-500/10 via-background to-fuchsia-500/10 flex items-center justify-center p-4"
      data-testid="trial-offer-screen"
    >
      <Card className="w-full max-w-md border-0 shadow-2xl overflow-hidden">
        <CardContent className="p-0">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-violet-500 to-fuchsia-500 p-6 text-center text-white">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2" data-testid="trial-title">
              Try Elite Free for 14 Days
            </h1>
            <p className="text-white/80 text-sm">
              Experience all premium features with no commitment
            </p>
          </div>

          <div className="p-6 space-y-5">
            {/* Features list */}
            <div className="space-y-3">
              {ELITE_FEATURES.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="text-sm font-medium">{feature.text}</span>
                  <Check className="w-4 h-4 text-emerald-500 ml-auto" />
                </div>
              ))}
            </div>

            {/* Trial info */}
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-violet-600">$0</p>
              <p className="text-xs text-muted-foreground">
                No credit card required • Cancel anytime
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button 
                className="w-full h-12 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                onClick={handleStartTrial}
                disabled={loading}
                data-testid="start-trial-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting Trial...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Free Trial
                  </>
                )}
              </Button>
              
              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground"
                onClick={onSkip}
                disabled={loading}
                data-testid="skip-trial-btn"
              >
                Continue with Free Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="text-[10px] text-center text-muted-foreground">
              After 14 days, you'll automatically switch to the free Essentials plan unless you upgrade.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
